/**
 * The run monitor.
 *
 * Live over SSE, because polling a run that takes minutes is either slow to update or
 * wasteful, and ADR-0035 established that a stream is inbound rather than egress.
 *
 * **The failure summary is shown, not hidden.** Three sources failing out of nine
 * produces `completed_partial` with a reason per source, and an analyst reading a report
 * needs to know which gaps are declared. A silent gap is worse than a stated one.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { CreateRunRequest, RunResponse, RunTask } from '@/lib/schemas';
import { Badge, Button, Card, Drawer, ErrorState, Loading, Table } from '@/components/ui';
import { useSSE } from '@/hooks/useSSE';
import { useCan } from '@/hooks/usePermissions';

interface RunEvent {
  status?: string;
  task?: string;
  detail?: string;
}

const TERMINAL = new Set(['complete', 'completed_partial', 'failed', 'cancelled']);

/**
 * The frames the run stream emits.
 *
 * Named, and `EventSource` delivers a named frame only to a listener registered for that
 * name — never to `onmessage`. Subscribing by name is therefore not optional: without it
 * the monitor connects and then shows an empty list forever.
 */
const RUN_EVENTS = ['run.state', 'run.finished'] as const;

/** The runs tab for one case. */
export function RunsTab({ caseId }: { caseId: string }): ReactElement {
  const canRun = useCan('create_run_cases');
  const queryClient = useQueryClient();
  const [watching, setWatching] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ['runs', caseId],
    queryFn: () => request<RunResponse[]>(`/cases/${caseId}/runs`),
    refetchInterval: 10_000,
  });

  const start = useMutation({
    mutationFn: () => request<RunResponse, CreateRunRequest>(`/cases/${caseId}/runs`, { method: 'POST', body: {} }),
    onSuccess: (run) => {
      setWatching(run.id);
      void queryClient.invalidateQueries({ queryKey: ['runs', caseId] });
    },
  });

  // **`RunStatus.CANCELLED` had no way to be reached.** A run against a slow source could
  // not be stopped from anywhere.
  const cancel = useMutation({
    mutationFn: (runId: string) => request<RunResponse>(`/runs/${runId}:cancel`, { method: 'POST' }),
    onSuccess: () => {
      setWatching(null);
      void queryClient.invalidateQueries({ queryKey: ['runs', caseId] });
    },
  });

  // The frames the run stream emits. Named, so they must be subscribed to by name —
  // `onmessage` never sees a named frame.
  const stream = useSSE<RunEvent>(watching ? `/runs/${watching}/events` : null, RUN_EVENTS);

  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState error={runs.error} />;

  return (
    <div className="space-y-4">
      {canRun && (
        <div className="flex items-center gap-3">
          <Button tone="primary" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'Starting…' : 'Start a run'}
          </Button>
          {start.error != null && <ErrorState error={start.error} />}
        </div>
      )}

      {watching && (
        <Card title={`Watching run ${watching.slice(0, 8)}`}>
          <p className="mb-2 text-xs text-muted">
            {stream.open ? 'Live.' : (stream.error ?? 'Stream closed.')}
          </p>
          <ol className="space-y-1 font-mono text-xs">
            {stream.events.map((event, index) => (
              <li key={index}>
                {event.status ?? event.task ?? ''} {event.detail ?? ''}
              </li>
            ))}
          </ol>
        </Card>
      )}

      <Table
        headers={['Run', 'Status', 'Started', 'Declared gaps']}
        empty="This case has not been run yet."
        rows={(runs.data ?? []).map((run) => [
          <span key="id" className="flex items-center gap-2">
            <button
              type="button"
              title="What each source did"
              onClick={() => setInspecting(run.id)}
              className="font-mono text-xs text-accent underline-offset-2 hover:underline"
            >
              {run.id.slice(0, 8)}
            </button>
            <Button onClick={() => setWatching(run.id)}>Watch</Button>
            {!TERMINAL.has(run.status) && canRun && (
              <Button
                data-testid={`cancel-run-${run.id}`}
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(run.id)}
              >
                Cancel
              </Button>
            )}
          </span>,
          <Badge
            key="s"
            tone={
              run.status === 'failed'
                ? 'danger'
                : run.status === 'completed_partial'
                  ? 'warn'
                  : TERMINAL.has(run.status)
                    ? 'ok'
                    : 'accent'
            }
          >
            {run.status}
          </Badge>,
          <span key="t" className="text-xs text-muted">
            {(run.started_at ?? '—').slice(0, 19).replace('T', ' ')}
          </span>,
          <span key="f" className="text-xs text-muted">
            {(run.failure_summary ?? [])
              .map((failure) => `${failure.plugin} (${failure.error_class})`)
              .join(', ') || '—'}
          </span>,
        ])}
      />

      {inspecting && <RunTasks runId={inspecting} onClose={() => setInspecting(null)} />}
    </div>
  );
}

/**
 * What each source did on one run.
 *
 * **The answer to "why did my run find nothing".** `/runs/{id}/tasks` records every source
 * dispatched with its outcome, its attempt count and, on failure, the class and detail —
 * and nothing rendered it, so the only way to tell a disabled source from a timeout from a
 * genuine "no results" was to read the database. `USER_GUIDE.md` §4 sends people here.
 */
function RunTasks({ runId, onClose }: { runId: string; onClose: () => void }): ReactElement {
  const run = useQuery({
    queryKey: ['run', runId],
    queryFn: () => request<RunResponse>(`/runs/${runId}`),
  });

  const tasks = useQuery({
    queryKey: ['run-tasks', runId],
    queryFn: () => request<RunTask[]>(`/runs/${runId}/tasks`),
  });

  return (
    <Drawer open title={`Run ${runId.slice(0, 8)}`} onClose={onClose}>
      {tasks.error != null && <ErrorState error={tasks.error} />}
      {tasks.isLoading && <Loading />}

      {run.data != null && (
        <p className="mb-3 text-xs text-muted">
          {run.data.status} · started {(run.data.started_at ?? '—').slice(0, 19).replace('T', ' ')}
        </p>
      )}

      <Table
        headers={['Source', 'Query type', 'Status', 'Attempt', 'Took', 'Why']}
        empty="This run dispatched nothing — no enabled source accepts this case's subjects."
        rows={(tasks.data ?? []).map((task) => [
          <span key="p" className="font-mono text-xs">
            {task.plugin_name}
          </span>,
          <span key="q" className="text-xs text-muted">
            {task.query_type}
          </span>,
          <Badge
            key="s"
            tone={task.status === 'succeeded' ? 'ok' : task.status === 'failed' ? 'danger' : 'muted'}
          >
            {task.status}
          </Badge>,
          <span key="a" className="text-xs text-muted">
            {task.attempt}
          </span>,
          <span key="d" className="text-xs text-muted">
            {task.duration_ms != null ? `${task.duration_ms} ms` : '—'}
          </span>,
          <span key="e" className="text-xs text-muted">
            {task.error_class != null ? `${task.error_class}: ${task.error_detail ?? ''}` : '—'}
          </span>,
        ])}
      />
    </Drawer>
  );
}
